import React, { Key, useMemo } from 'react';

import { EDataTestId } from '@webtrader/core/constants/test';

import ActionSheetBase from './Base';
import ActionSheetOptions from './Options';
import { IBaseActionSheetOption } from './Options/Selector';
import DefaultSelector, { ActionSheetSize } from './Selectors';
import SimpleSelector from './Selectors/Simple';

export type IActionSheetOption<T> = IBaseActionSheetOption<T>;

interface IProps<T> {
  size?: ActionSheetSize;
  title?: string;
  value?: T;
  simple?: boolean;
  disabled?: boolean;
  withBorder?: boolean;
  justifyCenter?: boolean;
  showActualValue?: boolean;
  options?: IActionSheetOption<T>[];
  testID?: EDataTestId;
  onChange?: (nextValue: T) => void;
  onToggleOn?: () => void;
}

const ActionSheet = <T extends Key | null | undefined>(props: IProps<T>) => {
  const {
    size = 'default',
    value,
    title,
    simple,
    disabled,
    withBorder = true,
    justifyCenter,
    showActualValue,
    options = [],
    testID,
    onChange,
    onToggleOn
  } = props;

  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const selector = useMemo(() => {
    if (simple) {
      return <SimpleSelector label={selectedOption?.label} />;
    }

    return (
      <DefaultSelector
        size={size}
        title={title}
        label={showActualValue ? selectedOption?.value?.toString() : selectedOption?.label}
        disabled={disabled}
        withBorder={withBorder}
        justifyCenter={justifyCenter}
      />
    );
  }, [
    simple,
    size,
    title,
    showActualValue,
    selectedOption?.value,
    selectedOption?.label,
    disabled,
    withBorder,
    justifyCenter
  ]);

  return (
    <ActionSheetBase selector={selector} disabled={disabled} onToggleOn={onToggleOn} testID={testID}>
      {({ onClose }) => {
        const onSelect = (value: T) => {
          onChange?.(value);
          onClose();
        };

        return <ActionSheetOptions value={value} options={options} onSelect={onSelect} justifyCenter={justifyCenter} />;
      }}
    </ActionSheetBase>
  );
};

export default ActionSheet;
